/* eslint-disable no-param-reassign */
import { Box, Label } from '@apia/theme';
import React, { MutableRefObject } from 'react';
import { formatMessage } from '../../../../../../utils/util';
import ApiaFilter from '../../../../ApiaFilter';
import controlDatesOrder from './controlDatesOrder';
import { TApiaFilter, TApiaFilterValue } from '../../../../../../types';
import store from '../../../../../../store';
import { useResponsiveTableContext } from '../../ResponsiveTableContext';

const DateToFilterRender = ({
  filter,
  filterTitle,
  lastEmittedValue,
  lastEmittedValueByPartner,
  avoidLabel = false,
}: {
  filter: TApiaFilter;
  filterTitle: string;
  lastEmittedValue: MutableRefObject<TApiaFilterValue | null>;
  lastEmittedValueByPartner: MutableRefObject<TApiaFilterValue | null>;
  avoidLabel?: boolean;
}) => {
  const {
    onFilterBlur,
    onFilterChange,
    onFilterPressEnter,
    onFilterError: onError,
    name,
  } = useResponsiveTableContext();
  const filterToId = filter.filterToId ?? `${filter.id}i`;

  const filterTo: TApiaFilter = store
    .getState()
    .querySlice.queries[
      name
    ]?.filters?.find((current) => current.apiaFilter.id === (filter.filterToId ?? `${filter.id}i`))
    ?.apiaFilter ?? {
    ...filter,
    hide: filter.filterToId === undefined,
    id: filterToId,
    currentValue: filter.filterToValue ?? '',
    required: filter.required,
  };

  const handleOnChange = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      const result = controlDatesOrder(
        (lastEmittedValue.current as string) !== ''
          ? (lastEmittedValue.current as string)
          : filter.currentValue !== ''
            ? (filter.currentValue as string)
            : '',
        currentValue as string,
      );
      if (result === true) {
        lastEmittedValueByPartner.current = currentValue;
        if (onFilterChange) {
          if (onError && filter.error) {
            onError({
              ...filter,
              error: undefined,
            });
          } else if (onError && filter.errorTo) {
            onError({
              ...filter,
              errorTo: undefined,
            });
          } else if (onError && filter.errorFrom) {
            onError({
              ...filter,
              errorFrom: undefined,
            });
          }
          void onFilterChange({
            ...filter,
            filterToId,
            filterToValue: currentValue,
            error: undefined,
            errorFrom: undefined,
            errorTo: undefined,
          });
        }
      } else {
        if (onError) {
          onError({
            ...filter,
            error: result,
          });
        }
      }
      return result;
    },
    [
      filter,
      filterToId,
      lastEmittedValue,
      lastEmittedValueByPartner,
      onError,
      onFilterChange,
    ],
  );

  const handleOnError = React.useCallback(
    (error: string, dateType?: 'from' | 'to') => {
      if (onError && !dateType) {
        onError({ ...filter, error: error });
      } else if (onError && dateType === 'from') {
        onError({ ...filter, errorFrom: error });
      } else if (onError && dateType === 'to') {
        onError({ ...filter, errorTo: error });
      }
    },
    [filter, onError],
  );
  const handleOnBlur = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      if (onFilterBlur)
        void onFilterBlur({
          ...filter,
          filterToId,
          filterToValue: currentValue,
          error: undefined,
          errorFrom: undefined,
          errorTo: undefined,
        });
    },
    [filter, filterToId, onFilterBlur],
  );
  const handleOnPressEnter = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      if (onFilterPressEnter)
        void onFilterPressEnter({
          ...filter,
          filterToId,
          filterToValue: currentValue,
        });
    },
    [filter, filterToId, onFilterPressEnter],
  );

  return (
    <Box
      className={
        filter.type === 'D' || filter.type === 'date' ? 'twoColumns' : ''
      }
    >
      {avoidLabel ? (
        <ApiaFilter
          filter={filterTo}
          shouldShowRedBorderOnRequired={false}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          onPressEnter={handleOnPressEnter}
          filterTo={true}
          onError={handleOnError}
        />
      ) : (
        <Label title={window.LBL_DATE_FILTER_TO_TOOLTIP}>
          <Box as="span">
            {formatMessage(window.LBL_DATE_FILTER_TO_TXT, {
              TOK1: filterTitle,
            })}
          </Box>
          <ApiaFilter
            filter={filterTo}
            onError={handleOnError}
            shouldShowRedBorderOnRequired={false}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onPressEnter={handleOnPressEnter}
            filterTo={true}
          />
        </Label>
      )}
    </Box>
  );
};

export default DateToFilterRender;
